import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import PreviewIcon from '@mui/icons-material/Preview';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  createLeadCampaign,
  generateLeadCampaign,
  updateLeadCampaign,
} from '../../api/leads';
import { LEAD_EMAIL_TEMPLATES } from '../../data/leadEmailTemplates';
import {
  LeadCampaignAudience,
  LeadCampaignAudiencePreview,
  LeadCampaignSchema,
  LeadEmailBrandAssets,
} from '../../data/types';
import { resolveLeadEmailHtml } from '../../utils/leadEmailHtml';
import { wrapLeadMergeTagsInHtml } from '../../utils/leadsAdmin';
import { isLeadEmailBodyEmpty, LeadEmailBodyEditor } from './LeadEmailBodyEditor';
import { LeadEmailBrandPanel } from './LeadEmailBrandPanel';
import { LeadMergeTagSubjectInput, LeadMergeTagText } from './LeadMergeTagText';

type LeadNewsletterComposerProps = {
  isOpen: boolean;
  adminEmail: string;
  recipientSummary: string;
  audience: LeadCampaignAudience;
  audiencePreview?: LeadCampaignAudiencePreview | null;
  initialCampaign?: LeadCampaignSchema | null;
  onClose: () => void;
  onSaved: (campaign: LeadCampaignSchema) => void;
  onRequestSend: (draft: {
    campaignId?: string;
    name: string;
    subject: string;
    messageHtml: string;
    useHomecookTemplate: boolean;
    brandAssets?: LeadEmailBrandAssets;
    audience: LeadCampaignAudience;
  }) => void;
  brandAssets?: LeadEmailBrandAssets;
  onBrandAssetsChange?: (assets: LeadEmailBrandAssets) => void;
  initialTemplateId?: string;
  persistToLeads?: boolean;
};

/** Wrap preview HTML in a lightweight HomeCook email shell for the composer preview pane. */
export const wrapLeadEmailPreviewShell = (innerHtml: string, useTemplate: boolean): string => {
  if (!useTemplate) return innerHtml;
  return `<div style="font-family:Arial,sans-serif;background:#F4F8F9;padding:24px;">
  <div style="max-width:600px;margin:0 auto;background:#fff;border-radius:12px;overflow:hidden;border:1px solid #E9E9E9;">
    <div style="background:#013847;color:#fff;padding:16px 24px;font-weight:700;">HomeCook</div>
    <div style="padding:24px;color:#2C3E50;line-height:1.6;">${innerHtml}</div>
    <div style="padding:16px 24px;background:#FAFAFA;color:#737373;font-size:12px;border-top:1px solid #E9E9E9;">
      © HomeCook · Shared commercial kitchen network
    </div>
  </div>
</div>`;
};

const formatBreakdownLine = (preview?: LeadCampaignAudiencePreview | null): string => {
  if (!preview?.breakdown) return '';
  const parts: string[] = [];
  const categories = Object.entries(preview.breakdown.by_category ?? {});
  if (categories.length) {
    parts.push(
      categories.map(([key, count]) => `${key.replace(/_/g, ' ')} (${count})`).join(', ')
    );
  }
  const cities = preview.breakdown.top_cities ?? [];
  if (cities.length) {
    parts.push(`Top cities: ${cities.map((item) => `${item.label} (${item.count})`).join(', ')}`);
  }
  return parts.join(' · ');
};

/** Full-screen campaign composer with templates, AI assist, and live preview. */
export const LeadNewsletterComposer: FC<LeadNewsletterComposerProps> = ({
  isOpen,
  adminEmail,
  recipientSummary,
  audience,
  audiencePreview,
  initialCampaign,
  onClose,
  onSaved,
  onRequestSend,
  brandAssets,
  onBrandAssetsChange,
  initialTemplateId,
  persistToLeads = true,
}) => {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [messageHtml, setMessageHtml] = useState('');
  const [useHomecookTemplate, setUseHomecookTemplate] = useState(true);
  const [campaignId, setCampaignId] = useState<string | undefined>();
  const [showPreview, setShowPreview] = useState(true);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiTone, setAiTone] = useState('professional');
  const [designStyle, setDesignStyle] = useState<'visual_newsletter' | 'plain_letter'>('visual_newsletter');
  const [includeVisuals, setIncludeVisuals] = useState(true);
  const [includeLeadContext, setIncludeLeadContext] = useState(true);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [undoSnapshot, setUndoSnapshot] = useState<{ subject: string; messageHtml: string } | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setName(initialCampaign?.name ?? '');
    setSubject(initialCampaign?.subject ?? '');
    setMessageHtml(initialCampaign?.message_html ?? '');
    setUseHomecookTemplate(initialCampaign?.use_homecook_template ?? true);
    setCampaignId(initialCampaign?.id);
    if (initialCampaign?.brand_assets && onBrandAssetsChange) {
      onBrandAssetsChange(initialCampaign.brand_assets);
    }
    setStatusMessage('');
    setUndoSnapshot(null);
    const templateId = initialCampaign ? null : (initialTemplateId || null);
    setActiveTemplateId(templateId);
    if (templateId) {
      const template = LEAD_EMAIL_TEMPLATES.find((item) => item.id === templateId);
      if (template) {
        setName(template.name);
        setSubject(template.subject);
        setMessageHtml(template.html);
      }
    }
  }, [initialCampaign, initialTemplateId, isOpen, onBrandAssetsChange]);

  const previewSampleLead = useMemo(
    () => audiencePreview?.sample_leads?.[0] ?? { first_name: 'Alex', business_name: 'Sample Kitchen', city: 'Washington' },
    [audiencePreview]
  );

  const previewHtml = useMemo(() => {
    const resolved = resolveLeadEmailHtml(messageHtml || '<p></p>', {
      lead: previewSampleLead,
      brandAssets,
      previewSample: true,
      stripDuplicateFooter: useHomecookTemplate,
    });
    return wrapLeadMergeTagsInHtml(wrapLeadEmailPreviewShell(resolved, useHomecookTemplate));
  }, [messageHtml, useHomecookTemplate, brandAssets, previewSampleLead]);

  const applyTemplate = useCallback((templateId: string) => {
    const template = LEAD_EMAIL_TEMPLATES.find((item) => item.id === templateId);
    if (!template) return;
    setUndoSnapshot({ subject, messageHtml });
    setSubject(template.subject);
    setMessageHtml(template.html);
    setActiveTemplateId(template.id);
    if (!name.trim()) setName(template.name);
    setDesignStyle(template.category === 'professional' ? 'plain_letter' : 'visual_newsletter');
    setStatusMessage(`Applied template: ${template.name}`);
  }, [messageHtml, name, subject]);

  const handleGenerate = async () => {
    if (!aiPrompt.trim()) {
      setStatusMessage('Enter a prompt to generate content.');
      return;
    }
    setAiLoading(true);
    try {
      const template = LEAD_EMAIL_TEMPLATES.find((item) => item.id === activeTemplateId);
      const result = await generateLeadCampaign(
        {
          prompt: aiPrompt.trim(),
          tone: aiTone,
          template_hint: template?.description ?? '',
          design_style: designStyle,
          include_visuals: includeVisuals,
          include_lead_context: includeLeadContext,
          current_subject: subject,
          current_message_html: messageHtml,
          audience,
          audience_context: audiencePreview
            ? {
                total: audiencePreview.total,
                audience_label: audiencePreview.audience_label,
                breakdown: audiencePreview.breakdown,
              }
            : undefined,
          lead_samples: audiencePreview?.sample_leads,
        },
        adminEmail
      );
      if (result?.subject || result?.message_html) {
        setUndoSnapshot({ subject, messageHtml });
        if (result.subject) setSubject(result.subject);
        if (result.message_html) setMessageHtml(result.message_html);
        setStatusMessage('AI draft generated. Use Undo if you want to revert.');
      }
    } catch {
      setStatusMessage('Could not generate content. Try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!persistToLeads) {
      setStatusMessage('Pipeline campaigns send directly to this resource’s partner referrals.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim() || subject.trim() || 'Untitled campaign',
        subject: subject.trim(),
        message_html: messageHtml,
        use_homecook_template: useHomecookTemplate,
        brand_assets: brandAssets,
        audience: {
          ...audience,
          resolved_count: audiencePreview?.total ?? audience.resolved_count,
        },
      };
      const saved = campaignId
        ? await updateLeadCampaign(campaignId, payload, adminEmail)
        : await createLeadCampaign(payload, adminEmail);
      if (saved?.id) {
        setCampaignId(saved.id);
        onSaved(saved);
        setStatusMessage('Draft saved.');
      }
    } catch {
      setStatusMessage('Could not save draft.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendClick = () => {
    if (!subject.trim()) {
      setStatusMessage('Subject is required.');
      return;
    }
    if (isLeadEmailBodyEmpty(messageHtml)) {
      setStatusMessage('Message body is required.');
      return;
    }
    onRequestSend({
      campaignId,
      name: name.trim() || subject.trim() || 'Untitled campaign',
      subject: subject.trim(),
      messageHtml,
      useHomecookTemplate,
      brandAssets,
      audience: {
        ...audience,
        resolved_count: audiencePreview?.total ?? audience.resolved_count,
      },
    });
  };

  if (!isOpen) return null;

  const breakdownLine = formatBreakdownLine(audiencePreview);

  return (
    <div className="fixed inset-0 z-[55] flex items-stretch justify-center bg-[#013847]/50 p-2 sm:p-4">
      <div className="relative flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-[#E9E9E9] bg-white shadow-2xl">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E9E9E9] bg-gradient-to-r from-[#EFF8FA] to-white px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#5DB6CE] font-raleway">
              Campaign composer
            </p>
            <h2 className="text-xl font-bold text-[#013847] font-raleway">
              {campaignId ? 'Edit campaign' : 'Create campaign'}
            </h2>
            <p className="mt-1 text-xs text-[#737373] font-open-sans">{recipientSummary}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={`rounded-lg px-3 py-2 text-xs font-semibold font-raleway ${
                showPreview ? 'bg-[#013847] text-white' : 'border border-[#E9E9E9] bg-white'
              }`}
              onClick={() => setShowPreview((current) => !current)}
            >
              <PreviewIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
              Preview
            </button>
            <button type="button" aria-label="Close composer" className="rounded-lg p-2 hover:bg-[#F5F5F5]" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>

        {statusMessage ? (
          <p className="border-b border-[#CFE8EF] bg-[#EFF8FA] px-5 py-2 text-sm font-open-sans">{statusMessage}</p>
        ) : null}

        <div className="grid min-h-0 flex-1 gap-0 lg:grid-cols-[220px_1fr_1fr]">
          <aside className="overflow-y-auto border-b border-[#E9E9E9] bg-[#FAFAFA] p-4 lg:border-b-0 lg:border-r">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#737373] font-raleway">Templates</p>
            <ul className="space-y-2">
              {LEAD_EMAIL_TEMPLATES.map((template) => (
                <li key={template.id}>
                  <button
                    type="button"
                    className={`w-full rounded-lg border px-3 py-2 text-left hover:border-[#5DB6CE] hover:bg-[#EFF8FA] ${
                      activeTemplateId === template.id ? 'border-[#5DB6CE] bg-[#EFF8FA]' : 'border-[#E9E9E9] bg-white'
                    }`}
                    onClick={() => applyTemplate(template.id)}
                  >
                    <p className="text-sm font-semibold text-[#013847] font-raleway">{template.name}</p>
                    <p className="text-xs text-[#737373] font-open-sans">{template.description}</p>
                  </button>
                </li>
              ))}
            </ul>

            {brandAssets && onBrandAssetsChange ? (
              <LeadEmailBrandPanel
                adminEmail={adminEmail}
                assets={brandAssets}
                onChange={onBrandAssetsChange}
              />
            ) : null}

            <div className="mt-6 rounded-xl border border-[#E9E9E9] bg-white p-3">
              <p className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-[#737373] font-raleway">
                <AutoAwesomeIcon sx={{ fontSize: 14 }} /> AI assist
              </p>
              {audiencePreview ? (
                <div className="mb-3 rounded-lg border border-[#CFE8EF] bg-[#EFF8FA] px-3 py-2 text-xs font-open-sans text-[#2C3E50]">
                  <p className="font-semibold text-[#013847]">
                    Audience: {audiencePreview.total} recipient{audiencePreview.total === 1 ? '' : 's'}
                  </p>
                  {audiencePreview.audience_label ? (
                    <p className="mt-1 text-[#737373]">{audiencePreview.audience_label}</p>
                  ) : null}
                  {breakdownLine ? <p className="mt-1 text-[#737373]">{breakdownLine}</p> : null}
                  {(audiencePreview.sample_leads?.length ?? 0) > 0 ? (
                    <p className="mt-1 text-[#737373]">
                      Sample goals:{' '}
                      {audiencePreview.sample_leads
                        ?.map((lead) => lead.goal || lead.lead_category || lead.city)
                        .filter(Boolean)
                        .slice(0, 3)
                        .join(' · ')}
                    </p>
                  ) : null}
                </div>
              ) : null}
              <textarea
                value={aiPrompt}
                onChange={(event) => setAiPrompt(event.target.value)}
                placeholder="Describe the newsletter you want…"
                className="mb-2 min-h-[80px] w-full rounded-lg border border-[#E9E9E9] px-3 py-2 text-sm font-open-sans"
              />
              <select
                value={aiTone}
                onChange={(event) => setAiTone(event.target.value)}
                className="mb-2 w-full rounded-lg border border-[#E9E9E9] px-3 py-2 text-sm font-open-sans"
              >
                <option value="professional">Professional</option>
                <option value="friendly">Friendly</option>
                <option value="enthusiastic">Enthusiastic</option>
                <option value="concise">Concise</option>
              </select>
              <select
                value={designStyle}
                onChange={(event) => setDesignStyle(event.target.value as 'visual_newsletter' | 'plain_letter')}
                className="mb-2 w-full rounded-lg border border-[#E9E9E9] px-3 py-2 text-sm font-open-sans"
              >
                <option value="visual_newsletter">Visual newsletter</option>
                <option value="plain_letter">Plain letter</option>
              </select>
              <label className="mb-2 flex items-center gap-2 text-xs font-open-sans">
                <input type="checkbox" checked={includeVisuals} onChange={(event) => setIncludeVisuals(event.target.checked)} />
                Include visuals / placeholders
              </label>
              <label className="mb-2 flex items-center gap-2 text-xs font-open-sans">
                <input
                  type="checkbox"
                  checked={includeLeadContext}
                  onChange={(event) => setIncludeLeadContext(event.target.checked)}
                />
                Use lead audience context
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={aiLoading}
                  className="flex-1 rounded-lg bg-[#013847] px-3 py-2 text-xs font-semibold text-white font-raleway disabled:opacity-60"
                  onClick={handleGenerate}
                >
                  {aiLoading ? 'Generating…' : 'Generate'}
                </button>
                {undoSnapshot ? (
                  <button
                    type="button"
                    className="rounded-lg border border-[#E9E9E9] px-3 py-2 text-xs font-semibold font-raleway"
                    onClick={() => {
                      setSubject(undoSnapshot.subject);
                      setMessageHtml(undoSnapshot.messageHtml);
                      setUndoSnapshot(null);
                      setStatusMessage('Reverted to previous content.');
                    }}
                  >
                    Undo
                  </button>
                ) : null}
              </div>
            </div>
          </aside>

          <section className="overflow-y-auto border-b border-[#E9E9E9] p-4 lg:border-b-0 lg:border-r">
            <label className="mb-3 block text-sm font-raleway">
              Campaign name
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Spring kitchen update"
                className="mt-1 w-full rounded-lg border border-[#E9E9E9] px-3 py-2 font-open-sans text-sm"
              />
            </label>
            <label className="mb-3 block text-sm font-raleway">
              Subject
              <LeadMergeTagSubjectInput
                value={subject}
                onChange={setSubject}
                placeholder="Subject line"
                className="mt-1"
                inputClassName="rounded-lg border border-[#E9E9E9] px-3 py-2"
              />
            </label>
            <p className="mb-3 flex flex-wrap gap-1.5 text-[11px] text-[#5F7078] font-open-sans">
              {[
                '{{first_name}}',
                '{{business_name}}',
                '{{city}}',
                '{{logo_url}}',
                '{{hero_image_url}}',
              ].map((token) => (
                <LeadMergeTagText key={token} value={token} />
              ))}
            </p>
            <div className="mb-3">
              <p className="mb-1 text-sm font-raleway">Message</p>
              <LeadEmailBodyEditor
                value={messageHtml}
                onChange={setMessageHtml}
                placeholder="Write your newsletter. Use {{first_name}}, {{business_name}}, {{city}}, and image placeholders."
                minHeightClass="min-h-[280px]"
                variant="campaign"
              />
            </div>
            <label className="flex items-start gap-2 text-sm font-open-sans">
              <input
                type="checkbox"
                checked={useHomecookTemplate}
                onChange={(event) => setUseHomecookTemplate(event.target.checked)}
                className="mt-0.5"
              />
              <span>Wrap in HomeCook email template (logo, header, footer)</span>
            </label>
          </section>

          {showPreview ? (
            <section className="overflow-y-auto bg-[#F4F8F9] p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[#737373] font-raleway">
                Live preview
              </p>
              <div
                className="mx-auto max-w-[600px] rounded-xl border border-[#E9E9E9] bg-white shadow-sm"
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </section>
          ) : (
            <section className="hidden items-center justify-center bg-[#FAFAFA] p-6 text-sm text-[#737373] font-open-sans lg:flex">
              Preview hidden — toggle Preview to see how recipients will view this email.
            </section>
          )}
        </div>

        <div className="flex flex-wrap gap-3 border-t border-[#E9E9E9] bg-[#FAFAFA] px-5 py-4">
          <button
            type="button"
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-xl border border-[#013847] px-4 py-2.5 text-sm font-semibold text-[#013847] font-raleway disabled:opacity-60"
            onClick={handleSaveDraft}
          >
            <SaveIcon sx={{ fontSize: 18 }} />
            {saving ? 'Saving…' : 'Save draft'}
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-xl bg-[#013847] px-4 py-2.5 text-sm font-semibold text-white font-raleway"
            onClick={handleSendClick}
          >
            <SendIcon sx={{ fontSize: 18 }} />
            Review & send
          </button>
        </div>
      </div>
    </div>
  );
};
